import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German',
  it: 'Italian', pt: 'Portuguese', ru: 'Russian', zh: 'Chinese',
  ja: 'Japanese', ko: 'Korean', ar: 'Arabic', tr: 'Turkish',
  hi: 'Hindi', nl: 'Dutch', pl: 'Polish', sv: 'Swedish',
  th: 'Thai', vi: 'Vietnamese', id: 'Indonesian', ms: 'Malay',
};

export const useProductTranslation = (products) => {
  const { i18n } = useTranslation();
  const [translatedProducts, setTranslatedProducts] = useState(products);
  const [translating, setTranslating] = useState(false);
  const cacheRef = useRef({});
  const currentLang = i18n.language;

  const translateProducts = useCallback(async (prods, lang) => {
    if (!prods || prods.length === 0) return prods;
    if (lang === 'en') return prods;

    const langName = LANGUAGE_NAMES[lang] || lang;
    const cacheKey = `${lang}_${prods.map(p => p.id).join(',')}`;

    if (cacheRef.current[cacheKey]) {
      return cacheRef.current[cacheKey];
    }

    try {
      setTranslating(true);
      const payload = prods.map(p => ({ id: p.id, name: p.name, description: p.description }));
      const res = await axios.post(`${API}/translate/products`, {
        products: payload,
        target_language: langName,
      });

      const translatedMap = {};
      (res.data.products || []).forEach(tp => { translatedMap[tp.id] = tp; });

      const result = prods.map(p => {
        const translated = translatedMap[p.id];
        return translated ? { ...p, name: translated.name, description: translated.description } : p;
      });

      cacheRef.current[cacheKey] = result;
      return result;
    } catch (err) {
      console.error('Product translation error:', err);
      return prods;
    } finally {
      setTranslating(false);
    }
  }, []);

  useEffect(() => {
    if (!products || products.length === 0) {
      setTranslatedProducts(products);
      return;
    }

    if (currentLang === 'en') {
      setTranslatedProducts(products);
      return;
    }

    translateProducts(products, currentLang).then(setTranslatedProducts);
  }, [products, currentLang, translateProducts]);

  return { translatedProducts, translating };
};
